import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

import {
  useDeleteContractInvoiceMutation,
  useGetContractInvoicesQuery,
  useGetCustomerQuery,
  useUpdateContractInvoicePaymentMutation,
} from "../../../Api/attoDeskApi";
import {
  IContractInvoice,
  ICustomer,
} from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { fmtCustomerName } from "../../../Core/utils/nameUtils";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v: any) => `$${(parseFloat(String(v || 0)) || 0).toFixed(2)}`;

const STATUS_MAP: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: "Draft",     color: "#64748b", bg: "#f1f5f9" },
  2: { label: "Finalized", color: "#d97706", bg: "#fef3c7" },
  3: { label: "Paid",      color: "#16a34a", bg: "#dcfce7" },
};

const statusOf = (inv: IContractInvoice) => {
  if (inv.amount_paid > 0 && inv.amount_paid >= inv.grand_total && inv.grand_total > 0)
    return STATUS_MAP[3];
  return STATUS_MAP[inv.status] ?? STATUS_MAP[1];
};

// ── Stat card ──────────────────────────────────────────────────────────────────
const StatCard = ({
  icon, label, value, color, bg,
}: { icon: React.ReactNode; label: string; value: string; color: string; bg: string }) => (
  <Box
    sx={{
      flex: 1, minWidth: 140,
      bgcolor: "#fff", borderRadius: 3,
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      p: 2, display: "flex", alignItems: "center", gap: 1.5,
    }}
  >
    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Box sx={{ color, fontSize: 20, lineHeight: 0 }}>{icon}</Box>
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{value}</Typography>
    </Box>
  </Box>
);

// ── Component ──────────────────────────────────────────────────────────────────
const ContractInvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const [searchQuery, setSearchQuery]       = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [startDate, setStartDate]           = useState("");
  const [endDate, setEndDate]               = useState("");

  // Payment dialog
  const [payDialogOpen, setPayDialogOpen]     = useState(false);
  const [payTarget, setPayTarget]             = useState<IContractInvoice | null>(null);
  const [payData, setPayData]                 = useState({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });


  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState<IContractInvoice | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 400);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const { data, isFetching, refetch } = useGetContractInvoicesQuery({
    searchText: debouncedSearch,
    customer_id: selectedCustomer || null,
    status: selectedStatus || null,
    startDate: startDate || null,
    endDate: endDate || null,
  });

  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "" });

  const [deleteContractInvoice, { isLoading: isDeleting }] = useDeleteContractInvoiceMutation();
  const [updatePayment, { isLoading: isUpdatingPayment }]  = useUpdateContractInvoicePaymentMutation();

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim())     params.search      = searchQuery.trim();
      if (startDate)              params.start_date  = startDate;
      if (endDate)                params.end_date    = endDate;
      if (selectedCustomer)       params.customer_id = selectedCustomer;
      if (selectedStatus)         params.status      = selectedStatus;
      const response = await axios.get(`${API_URL}/pdf/contract-invoices-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contract-invoices-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      try {
        const blob: Blob = err?.response?.data;
        const text = blob ? await blob.text() : "";
        const parsed = JSON.parse(text || "{}");
        showErrorMessage(parsed?.message || "Failed to generate print report.");
      } catch {
        showErrorMessage("Failed to generate print report.");
      }
    } finally { setPdfLoading(false); }
  };

  const handleExportToExcel = (rows: IContractInvoice[]) => {
    const data = rows.map((inv, i) => {
      const balance = Math.max(0, parseFloat(String(inv.grand_total ?? 0)) - parseFloat(String(inv.amount_paid ?? 0)));
      const client = fmtCustomerName(inv.customer, "");
      return {
        "#":             i + 1,
        "Invoice No":    inv.contract_invoice_no || "",
        "PO Number":     inv.po_number || "",
        "Issued By":     inv.company?.companyName || "",
        "Client":        client,
        "Entries":       inv.entries?.length ?? 0,
        "Issue Date":    inv.issue_date ? dayjs(inv.issue_date).format("DD MMM YYYY") : "",
        "Due Date":      inv.due_date  ? dayjs(inv.due_date).format("DD MMM YYYY")   : "",
        "Total":         parseFloat(String(inv.grand_total ?? 0)).toFixed(2),
        "Paid":          parseFloat(String(inv.amount_paid ?? 0)).toFixed(2),
        "Balance":       balance.toFixed(2),
        "Status":        statusOf(inv).label,
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contract Invoices");
    XLSX.writeFile(wb, `contract_invoices_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const invoices: IContractInvoice[] = useMemo(
    () => (data?.data as IContractInvoice[]) ?? [],
    [data]
  );

  const customers: ICustomer[] = useMemo(
    () => (customerData?.data as ICustomer[]) ?? [],
    [customerData]
  );


  const handlePrint = (invoiceId: number | string) => {
    // Open window synchronously — avoids popup blocker.
    const win = window.open("", "_blank", "width=900,height=750");
    if (!win) return;

    win.document.write(`<!DOCTYPE html><html><head><title>Contract Invoice</title>
      <style>
        body { font-family: Arial, sans-serif; display: flex; align-items: center;
               justify-content: center; height: 100vh; margin: 0;
               color: #64748b; font-size: 14px; background: #f8fafc; }
        .spinner { border: 3px solid #e2e8f0; border-top-color: #1e2751;
                   border-radius: 50%; width: 32px; height: 32px;
                   animation: spin 0.8s linear infinite; margin-right: 12px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .msg { display: flex; align-items: center; }
      </style></head>
      <body><div class="msg"><div class="spinner"></div><span>Generating invoice, please wait…</span></div></body></html>`);
    win.document.close();

    const token = localStorage.getItem("token") || "";

    fetch(`${API_URL}/pdf/contract-invoice/${invoiceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Server returned " + res.status);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        win.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch(() => {
        if (!win.closed) {
          win.document.open();
          win.document.write(`<!DOCTYPE html><html><body style="font-family:Arial;padding:40px;color:#dc2626;">
            <h3>Failed to generate invoice</h3><p>Please try again or contact support.</p>
            </body></html>`);
          win.document.close();
        }
      });
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  const { totalCount, totalRevenue, totalPaid, totalOutstanding } = useMemo(() => {
    let totalRevenue = 0, totalPaid = 0;
    invoices.forEach((inv) => {
      totalRevenue    += parseFloat(String(inv.grand_total ?? 0));
      totalPaid       += parseFloat(String(inv.amount_paid ?? 0));
    });
    return {
      totalCount:       invoices.length,
      totalRevenue,
      totalPaid,
      totalOutstanding: Math.max(0, totalRevenue - totalPaid),
    };
  }, [invoices]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteContractInvoice(String(deleteTarget.id)).unwrap();
      showMessage("Invoice deleted", "success");
      setDeleteDialogOpen(false);
      refetch();
    } catch {
      showMessage("Failed to delete", "error");
    }
  };

  const handlePaymentSave = async () => {
    if (!payTarget || !payData.amount || !payData.method) {
      showMessage("Please fill in amount and payment method", "error");
      return;
    }
    const entered = parseFloat(payData.amount);
    if (isNaN(entered) || entered <= 0) { showMessage("Invalid amount", "error"); return; }
    const currentPaid = parseFloat(String(payTarget.amount_paid ?? 0));
    const balance     = Math.max(0, parseFloat(String(payTarget.grand_total ?? 0)) - currentPaid);
    if (entered > balance + 0.001) { showMessage(`Amount exceeds balance (${fmt(balance)})`, "error"); return; }
    try {
      await updatePayment({ id: payTarget.id, amount_paid: currentPaid + entered }).unwrap();
      showMessage("Payment recorded", "success");
      setPayDialogOpen(false);
      setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
      refetch();
    } catch {
      showMessage("Failed to record payment", "error");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#0f172a">
          Contract Invoices
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/contract-invoice/create")}
          sx={{
            textTransform: "none", borderRadius: 2, px: 2.5,
            bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" },
          }}
        >
          New Contract Invoice
        </Button>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <StatCard icon={<ReceiptLongOutlinedIcon />}         label="Total Invoices"   value={String(totalCount)}         color="#2f3891" bg="#eef2ff" />
        <StatCard icon={<AttachMoneyIcon />}                  label="Total Revenue"    value={fmt(totalRevenue)}           color="#16a34a" bg="#dcfce7" />
        <StatCard icon={<AccountBalanceWalletOutlinedIcon />} label="Total Collected"  value={fmt(totalPaid)}              color="#0ea5e9" bg="#e0f2fe" />
        <StatCard icon={<PendingActionsOutlinedIcon />}       label="Outstanding"      value={fmt(totalOutstanding)}       color="#d97706" bg="#fef3c7" />
      </Box>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search invoice no…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Draft</MenuItem>
            <MenuItem value="2">Finalized</MenuItem>
            <MenuItem value="3">Paid</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Client"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Clients</MenuItem>
            {customers.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {fmtCustomerName(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          {(selectedStatus || selectedCustomer || startDate || endDate || searchQuery) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedStatus(""); setSelectedCustomer("");
                setStartDate(""); setEndDate(""); setSearchQuery("");
              }}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Clear
            </Button>
          )}
          <Button size="small" variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <LocalPrintshopSharpIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownloadPdf} disabled={pdfLoading}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}>
            {pdfLoading ? "Generating…" : "Print"}
          </Button>
          {invoices.length > 0 && (
            <Button size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => handleExportToExcel(invoices)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}>
              Excel
            </Button>
          )}
        </Box>
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#2f3891" }} />
          </Box>
        ) : invoices.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <Typography color="text.secondary">No contract invoices found</Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 2, textTransform: "none", bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}
              onClick={() => navigate("/contract-invoice/create")}
            >
              Create your first one
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Invoice No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Issued By</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="center">Entries</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Issue Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Balance</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => {
                  const st = statusOf(inv);
                  const clientName = inv.customer
                    ? fmtCustomerName(inv.customer, `Client #${inv.customer_id}`)
                    : `Client #${inv.customer_id}`;
                  const balance = Math.max(0, parseFloat(String(inv.grand_total ?? 0)) - parseFloat(String(inv.amount_paid ?? 0)));

                  return (
                    <TableRow key={inv.id} hover>
                      <TableCell>
                        <Typography fontWeight={600} fontSize={13} color="#2f3891">
                          {inv.contract_invoice_no}
                        </Typography>
                        {inv.po_number && (
                          <Typography fontSize={11} color="text.secondary">PO: {inv.po_number}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {inv.company?.companyName ? (() => {
                          const name: string = inv.company.companyName;
                          const isBusLines = name.toLowerCase().includes("bus line");
                          return (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.25,
                                py: 0.4,
                                borderRadius: "6px",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                whiteSpace: "nowrap",
                                bgcolor: isBusLines ? "#dbeafe" : "#dcfce7",
                                color: isBusLines ? "#1e40af" : "#15803d",
                              }}
                            >
                              {name}
                            </Box>
                          );
                        })() : "—"}
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500}>{clientName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={inv.entries?.length ?? 0}
                          size="small"
                          sx={{ bgcolor: "#eef2ff", color: "#2f3891", fontWeight: 600, fontSize: 12 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>
                          {inv.issue_date ? dayjs(inv.issue_date).format("MMM D, YYYY") : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>
                          {inv.due_date ? dayjs(inv.due_date).format("MMM D, YYYY") : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600} fontSize={13}>{fmt(inv.grand_total)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize={13} color="#16a34a">{fmt(inv.amount_paid)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize={13} color={balance > 0 ? "#d97706" : "#16a34a"} fontWeight={600}>
                          {fmt(balance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={st.label}
                          size="small"
                          sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          {/* View */}
                          <Tooltip title="View invoice">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/contract-invoice/view/${inv.id}`)}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f8fafc", color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}
                            >
                              <VisibilitySharpIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Print */}
                          <Tooltip title="Print invoice">
                            <IconButton
                              size="small"
                              onClick={() => handlePrint(inv.id)}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0f9ff", color: "#0ea5e9", "&:hover": { bgcolor: "#e0f2fe" } }}
                            >
                              <LocalPrintshopSharpIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Record payment */}
                          <Tooltip title="Record payment">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPayTarget(inv);
                                setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
                                setPayDialogOpen(true);
                              }}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0fdf4", color: "#16a34a", "&:hover": { bgcolor: "#dcfce7" } }}
                            >
                              <AttachMoneyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Edit */}
                          <Tooltip title="Edit invoice">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/contract-invoice/edit/${inv.id}`)}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#2f3891", "&:hover": { bgcolor: "#e0e7ff" } }}
                            >
                              <EditSharpIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Delete */}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => { setDeleteTarget(inv); setDeleteDialogOpen(true); }}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#fff5f5", color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>


      {/* ── Payment dialog ── */}
      <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#10b981" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Make a Payment</Typography>
          <IconButton size="small" onClick={() => setPayDialogOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {payTarget && (
            <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, px: 2, py: 1.25, display: "flex", flexDirection: "column", gap: 0.25 }}>
              {[
                { label: "Invoice",     value: payTarget.contract_invoice_no },
                { label: "Grand Total", value: fmt(payTarget.grand_total) },
                { label: "Paid",        value: fmt(payTarget.amount_paid) },
                { label: "Balance Due", value: fmt(Math.max(0, parseFloat(String(payTarget.grand_total ?? 0)) - parseFloat(String(payTarget.amount_paid ?? 0)))) },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography fontSize={12} color="text.secondary">{label}</Typography>
                  <Typography fontSize={12} fontWeight={700}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Date</Typography>
              <TextField size="small" type="date" fullWidth value={payData.date}
                onChange={(e) => setPayData({ ...payData, date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Amount</Typography>
              <TextField size="small" type="text" inputMode="decimal" fullWidth placeholder="0.00"
                value={payData.amount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setPayData({ ...payData, amount: v });
                }} />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Payment Method</Typography>
            <Select size="small" fullWidth displayEmpty value={payData.method}
              onChange={(e) => setPayData({ ...payData, method: e.target.value as string })}>
              <MenuItem value="" disabled><em>Select method</em></MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Debit Card">Debit Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Cheque">Cheque</MenuItem>
              <MenuItem value="Purchase Order (PO)">Purchase Order (PO)</MenuItem>
              <MenuItem value="E-Transfer">E-Transfer</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Description</Typography>
            <TextField size="small" fullWidth multiline rows={2} placeholder="Optional note…"
              value={payData.description}
              onChange={(e) => setPayData({ ...payData, description: e.target.value })} />
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setPayDialogOpen(false)} disabled={isUpdatingPayment}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handlePaymentSave}
            disabled={isUpdatingPayment || !payData.amount || !payData.method}
            sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 1.5, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, boxShadow: "none", minWidth: 80 }}>
            {isUpdatingPayment ? <CircularProgress size={18} color="inherit" /> : "Pay"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.contract_invoice_no}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
            sx={{ textTransform: "none" }}
          >
            {isDeleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractInvoiceList;
